import CoinsSvg from '@/shared/assets/images/Coins.svg'
import TransferSvg from '@/shared/assets/images/ArrowsLeftRight.svg'
import { selectBonuses } from '@/entities/bonus'
import { calculateFoundationBuyPreview, fetchTradingWallet, selectTradingWallet } from '@/entities/trading'
import { useAppDispatch, useAppSelector } from '@/shared/lib/redux'
import { useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { createOrderValidationSchema, fetchCreateOrder, selectPair } from '../../model'
import { yupResolver } from '@hookform/resolvers/yup'
import ReactSelect, { GroupBase, OptionsOrGroups } from 'react-select'
import ArrowDown from '@/shared/assets/images/ArrowDown.svg'
import { fetchWallets } from '@/entities/community'
import notify from '@/shared/lib/notify'
import { getErrorMessage } from '@/shared/consts/error-codes'
import formatMediumFont from '@/shared/lib/format-medium-font'
import { toLocaleString } from '@/shared/lib/to-locale-string'

interface CreateBuyOrderFormValues {
    ord_type: "limit" | "market",
    price: number,
    quantity: number
}

const initialState: CreateBuyOrderFormValues = {
    ord_type: "limit",
    quantity: null,
    price: 1
}

const ordTypeSelectOptions: OptionsOrGroups<any, GroupBase<any>> = [
    { value: "limit", label: "Лимитный" },
    { value: "market", label: "Рычночный" }
]

export const CreateBuyOrder = () => {
    const dispatch = useAppDispatch()
    const tradingWallet = useAppSelector(selectTradingWallet)
    const bonuses = useAppSelector(selectBonuses)
    const pair = useAppSelector(selectPair)
    const balance = useMemo(() => {
        const wallet = tradingWallet[pair.bonus2_id]
        return parseFloat(((wallet?.amount || 0) - (wallet?.blocked || 0)).toFixed(2))
    }, [tradingWallet, pair])
    const bonus1 = useMemo(() => bonuses.find(bonus => bonus.id === pair.bonus1_id), [bonuses, pair])
    const bonus2 = useMemo(() => bonuses.find(bonus => bonus.id === pair.bonus2_id), [bonuses, pair])

    const onSubmit = (data: CreateBuyOrderFormValues) => {
        dispatch(fetchCreateOrder(data))
            .unwrap()
            .then(() => {
                notify.success('Успешно')
                reset()
                dispatch(fetchWallets())
                dispatch(fetchTradingWallet())
            })
            .catch((error) => {
                notify.error(getErrorMessage(error))
            })
    }

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        control,
        watch,
        getValues
    } = useForm({
        defaultValues: initialState,
        resolver: yupResolver(createOrderValidationSchema)
    })

    const { receiveAmount, expectedFee } = useMemo(() => {
        const quantity = Number(getValues('quantity'))
        const price = Number(getValues('price'))
        return calculateFoundationBuyPreview(quantity, price, pair.fee)
    }, [watch('quantity'), watch('price'), pair.fee])

    return (
        <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-4'>
            <div className={'rounded-2xl screen:rounded-3xl transition-all ease-in bg-white w-full px-4 py-5 screen:px-6 screen:py-7 flex justify-between items-center'}>
                <div className='flex items-center gap-3'>
                    <div className={'bg-color-dark overflow-hidden transition-all ease-in rounded-full w-[56px] h-[56px] flex items-center justify-center text-color-primary'}>
                        <div className="fill-color-dark text-color-primary">
                            <CoinsSvg width={32} height={32} />
                        </div>
                    </div>
                    <div className={'text-color-base transition-all ease-in'}>
                        <div className='text-base leading-[16px] screen:text-lg uppercase screen:leading-[25px] mb-2 screen:mb-2'>Баланс</div>
                        <div className='text-2xl screen:text-[32px] leading-[24px] font-mediumttf screen:leading-[32px] font-semibold'>{formatMediumFont(toLocaleString(balance), 'text-3xl')}</div>
                    </div>
                </div>
            </div>
            <div className={'rounded-2xl justify-end screen:rounded-3xl transition-all relative ease-in gap-3 bg-color-dark w-full pr-4 py-5 screen:px-6 screen:py-7 flex items-center'}>
                <Controller
                    control={control}
                    name={'ord_type'}
                    render={({ field }) => (
                        <ReactSelect
                            {...field}
                            className={`w-full left-0 right-0 text-color-dark !absolute flex items-center py-5 pl-5 screen:pl-6 pr-[90px] h-[52px] bg-color-dark`}
                            options={ordTypeSelectOptions}
                            classNames={{
                                input: () => '!text-white font-mediumttf placeholder:!text-white placeholder:!font-mediumttf placeholder:!text-[22px] text-[22px]',
                                control: () => '!border-0 !p-0 !bg-transparent !text-white !outline-0 !min-h-[auto] w-full !border-transparent !shadow-none',
                                valueContainer: () => '!border-0 !outline-0 !p-0',
                                menu: () => '!left-[0]',
                                singleValue: () => '!text-white !font-mediumttf !text-[22px]'
                            }}
                            name={field.name}
                            ref={field.ref}
                            classNamePrefix="react_select_trading"
                            noOptionsMessage={() => 'Нет опций'}
                            components={{
                                DropdownIndicator: () => <ArrowDown height={20} width={20} />,
                                IndicatorSeparator: () => null,
                            }}
                            value={ordTypeSelectOptions.find(option => option.value === field.value)}
                            onChange={(e) => field.onChange(e.value)}
                        />
                    )}
                />
                <div className={'bg-color-primary z-10 shrink-0 overflow-hidden transition-all ease-in rounded-full w-[56px] h-[56px] flex items-center justify-center text-color-primary'}>
                    <div className="fill-color-dark text-color-dark">
                        <TransferSvg width={32} height={32} />
                    </div>
                </div>
            </div>
            <div className={'rounded-2xl screen:rounded-3xl transition-all ease-in gap-3 bg-color-dark w-full px-4 py-5 screen:px-6 screen:py-7 justify-between flex items-center'}>
                <div className={'text-color-base transition-all ease-in'}>
                    <div className='flex gap-3 justify-between'>
                        <div className={`text-base text-white/70 leading-[16px] screen:text-lg screen:leading-[25px]`}>ЦЕНА</div>
                        {errors['price'] &&
                            <label className="text-sm font-semibold uppercase flex items-center text-color-danger">
                                {errors['price'].message}
                            </label>
                        }
                    </div>
                    <input
                        className='text-[22px] outline-none disabled:text-white/10 placeholder:text-[22px] placeholder:leading-[22px] py-0 appearance-none [&::-webkit-inner-spin-button]:hidden text-white w-full font-mediumttf bg-transparent placeholder:text-white/20 screen:text-[32px] font-semibold'
                        placeholder="ВВЕСТИ ЦЕНУ"
                        {...register('price')}
                    />
                </div>
                <div className={`bg-color-primary shrink-0 overflow-hidden transition-all ease-in rounded-full w-[56px] h-[56px] flex items-center justify-center text-color-primary`}>
                    <div className="fill-color-dark text-color-dark">
                        <CoinsSvg width={32} height={32} />
                    </div>
                </div>
            </div>
            <div className={'rounded-2xl screen:rounded-3xl transition-all ease-in gap-3 bg-color-dark w-full px-4 py-5 screen:px-6 screen:py-7 justify-between flex items-center'}>
                <div className={'text-color-base transition-all ease-in'}>
                    <div className='flex gap-3 justify-between'>
                        <div className='text-base text-white/70 leading-[16px] screen:text-lg screen:leading-[25px]'>ВВЕДИТЕ СУММУ</div>
                        {errors['quantity'] &&
                            <label className="text-sm font-semibold uppercase flex items-center text-color-danger">
                                {errors['quantity'].message}
                            </label>
                        }
                    </div>
                    <input
                        className='text-[22px] outline-none placeholder:text-[22px] placeholder:leading-[22px] py-0 appearance-none [&::-webkit-inner-spin-button]:hidden text-white w-full font-mediumttf bg-transparent placeholder:text-white/20 screen:text-[32px] font-semibold'
                        placeholder="ВВЕСТИ СУММУ"
                        {...register('quantity')}
                    />
                </div>
                <div className={'bg-white shrink-0 overflow-hidden transition-all ease-in rounded-full w-[56px] h-[56px] flex items-center justify-center text-color-primary'}>
                    <div className={'bgwhite overflow-hidden transition-all ease-in rounded-full w-[56px] h-[56px] flex items-center justify-center text-color-primary'}>
                        <div className="w-full h-full">
                            <img
                                src={bonus2?.image}
                                className="object-contain w-full h-full"
                            />
                        </div>
                    </div>
                </div>
            </div>
            {expectedFee !== null && expectedFee > 0 &&
                <div className={'rounded-2xl screen:rounded-3xl transition-all ease-in gap-3 bg-color-dark w-full px-4 py-5 screen:px-6 screen:py-7 justify-between flex items-center'}>
                    <div className={'text-color-base transition-all ease-in'}>
                        <div className='flex gap-3 justify-between'>
                            <div className='text-base text-white/70 leading-[16px] screen:text-lg screen:leading-[25px]'>КОМИССИЯ</div>
                        </div>
                        <input
                            className='text-[22px] outline-none placeholder:text-[22px] placeholder:leading-[22px] py-0 appearance-none [&::-webkit-inner-spin-button]:hidden text-white w-full font-mediumttf bg-transparent placeholder:text-white/20 screen:text-[32px] font-semibold'
                            placeholder="КОМИССИЯ"
                            disabled
                            value={expectedFee}
                        />
                    </div>
                    <div className={'bg-white shrink-0 overflow-hidden transition-all ease-in rounded-full w-[56px] h-[56px] flex items-center justify-center text-color-primary'}>
                        <div className={'bgwhite overflow-hidden transition-all ease-in rounded-full w-[56px] h-[56px] flex items-center justify-center text-color-primary'}>
                            <div className="w-full h-full">
                                <img
                                    src={bonus2?.image}
                                    className="object-contain w-full h-full"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            }
            <div className={'rounded-2xl screen:rounded-3xl transition-all ease-in gap-3 bg-color-dark w-full px-4 py-5 screen:px-6 screen:py-7 justify-between flex items-center'}>
                <div className={'text-color-base transition-all ease-in'}>
                    <div className='flex gap-3 justify-between'>
                        <div className='text-base text-white/70 leading-[16px] screen:text-lg screen:leading-[25px]'>ВЫ КУПИТЕ</div>
                    </div>
                    <input
                        className='text-[22px] outline-none placeholder:text-[22px] placeholder:leading-[22px] py-0 appearance-none [&::-webkit-inner-spin-button]:hidden text-white w-full font-mediumttf bg-transparent placeholder:text-white/20 screen:text-[32px] font-semibold'
                        placeholder="СУММА ПОКУПКИ"
                        disabled
                        value={receiveAmount ?? ''}
                    />
                </div>
                <div className={'bg-white shrink-0 overflow-hidden transition-all ease-in rounded-full w-[56px] h-[56px] flex items-center justify-center text-color-primary'}>
                    <div className={'bgwhite overflow-hidden transition-all ease-in rounded-full w-[56px] h-[56px] flex items-center justify-center text-color-primary'}>
                        <div className="w-full h-full">
                            <img
                                src={bonus1?.image}
                                className="object-contain w-full h-full"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <button type='submit' className={'rounded-2xl screen:rounded-3xl transition-all ease-in gap-3 bg-color-primary w-full px-4 py-5 screen:px-6 screen:py-7 flex items-center'}>
                <div className={'bg-color-dark shrink-0 overflow-hidden transition-all ease-in rounded-full w-[56px] h-[56px] flex items-center justify-center'}>
                    <div className="fill-color-primary text-color-primary">
                        <CoinsSvg width={32} height={32} />
                    </div>
                </div>
                <div className={'text-color-base transition-all ease-in'}>
                    <div className='text-[22px] text-color-dark font-mediumttf screen:text-[26px] leading-[22px] screen:leading-[26px] font-semibold'>
                        КУПИТЬ
                    </div>
                </div>
            </button>
        </form >
    )
}